<template>
  <div class="vac-room-container">
    <slot :name="'room-list-item_' + room.roomId">
      <slot :name="'room-list-avatar_' + room.roomId">
        <el-image
            v-if="hasCustomAvatar"
            class="vac-avatar vac-avatar-image"
            :src="resolvedAvatar"
            fit="cover"
        >
          <template #error>
            <div
                class="vac-avatar"
                :style="{ 'background-image': `url('${fallbackAvatar}')` }"
            />
          </template>
        </el-image>
        <div
            v-else
            class="vac-avatar"
            :style="{ 'background-image': `url('${resolvedAvatar}')` }"
        />
      </slot>
      <div class="vac-name-container vac-text-ellipsis">
        <div class="vac-title-container">
          <slot :name="'room-list-info_' + room.roomId">
            <div class="vac-room-name vac-text-ellipsis" style="display: flex;align-items: center;margin-top: 4px;">
              <span>{{ displayRoomName }}</span>
            </div>
          </slot>
          <div v-if="room.lastMessage" class="vac-text-date">
            {{ formatChatTime(room.lastMessage.timestamp).date }}
          </div>
        </div>
        <div
            class="vac-text-last"
            :class="{
						'vac-message-new':
							room.lastMessage && room.lastMessage.new && !typingUsers
					}"
        >
					<span v-if="isMessageCheckmarkVisible">
						<slot :name="'checkmark-icon_' + room.roomId">
							<svg-icon
                  :name="
									room.lastMessage.distributed
										? 'double-checkmark'
										: 'checkmark'
								"
                  :param="room.lastMessage.seen ? 'seen' : ''"
                  class="vac-icon-check"
              />
						</slot>
					</span>
          <div
              v-if="room.lastMessage && !room.lastMessage.deleted && isAudio"
              class="vac-text-ellipsis"
          >
            <slot :name="'microphone-icon_' + room.roomId">
              <svg-icon name="microphone" class="vac-icon-microphone"/>
            </slot>
            {{ formattedDuration }}
          </div>
          <format-message
              v-else-if="room.lastMessage"
              :message-id="room.lastMessage._id"
              :room-id="room.roomId"
              :room-list="true"
              :content="getLastMessage"
              :deleted="!!room.lastMessage.deleted && !typingUsers"
              :users="room.users"
              :text-messages="textMessages"
              :linkify="false"
              :text-formatting="textFormatting"
              :link-options="linkOptions"
              :single-line="true"
          >
            <template v-for="(idx, name) in $slots" #[name]="data">
              <slot :name="name" v-bind="data"/>
            </template>
          </format-message>
          <div
              v-if="!room.lastMessage && typingUsers"
              class="vac-text-ellipsis"
          >
            {{ typingUsers }}
          </div>
          <div class="vac-room-options-container">
            <div
                v-if="room.unreadCount"
                class="vac-badge-counter vac-room-badge"
            >
              {{ room.unreadCount }}
            </div>
            <slot :name="'room-list-options_' + room.roomId">
              <div
                  v-if="userStatus==='online'"
                  class="vac-state-circle"
                  :class="{ 'vac-state-online': userStatus === 'online' }"
              />
              <div
                  v-if="room.isPinned" class="pin-top">
                <svg t="1773227567808" class="icon" viewBox="0 0 1024 1024" version="1.1"
                     xmlns="http://www.w3.org/2000/svg" p-id="5377" width="14" height="14">
                  <path
                      d="M648.728381 130.779429a73.142857 73.142857 0 0 1 22.674286 15.433142l191.561143 191.756191a73.142857 73.142857 0 0 1-22.137905 118.564571l-67.876572 30.061715-127.341714 127.488-10.093714 140.239238a73.142857 73.142857 0 0 1-124.684191 46.445714l-123.66019-123.782095-210.724572 211.699809-51.833904-51.614476 210.846476-211.821714-127.926857-128.024381a73.142857 73.142857 0 0 1 46.299428-124.635429l144.237715-10.776381 125.074285-125.220571 29.379048-67.779048a73.142857 73.142857 0 0 1 96.207238-38.034285z"
                      p-id="5378" fill="#B3B3B3"></path>
                </svg>
              </div>
              <div v-if="room.isSpecial" class="special-attention">
                <svg t="1773648231761" class="icon" viewBox="0 0 1065 1024" version="1.1"
                     xmlns="http://www.w3.org/2000/svg" p-id="6709" width="10" height="10">
                  <path
                      d="M1050.73523 272.085521h-0.243259c-16.663262-49.503266-44.88134-94.506236-81.491864-130.630241-37.583561-36.367265-83.194679-64.342083-133.306093-80.640456-58.138971-18.001188-115.791424-18.609336-169.916617-4.621927-48.043711 12.771113-92.803421 36.975413-132.941205 69.815418-39.894524-32.840005-85.019123-57.044305-133.306093-69.815418-53.273785-13.987409-111.169498-13.987409-169.06521 4.621927-49.868155 15.690224-95.844162 44.273192-132.576315 80.032307-37.218672 36.367265-66.288158 81.735123-82.951419 131.23839-18.609336 56.436156-19.217484 112.264164-5.230075 164.443283 14.473928 54.733341 45.124599 104.723126 87.573346 147.171873L503.766707 990.065328v-0.608148c16.298373 16.906521 41.840599 16.906521 57.652453 0.608148l405.026725-404.418577 2.554222-2.067704c41.962228-42.448747 72.49127-92.560162 86.965198-147.171873 13.987409-52.057489 13.379261-107.885497-5.230075-164.321653z"
                      p-id="6710" fill="#428bee"></path>
                </svg>
              </div>
              <div
                  v-if="filteredRoomActions.length"
                  class="vac-svg-button vac-list-room-options"
                  @click.stop="openRoomMenu()"
              >
                <slot :name="'room-list-options-icon_' + room.roomId">
                  <svg-icon name="dropdown" param="room"/>
                </slot>
              </div>
              <transition v-if="filteredRoomActions.length" name="vac-slide-left">
                <div
                    v-if="roomMenuOpened === room.roomId"
                    v-click-outside="closeRoomMenu"
                    class="vac-menu-options"
                >
                  <div class="vac-menu-list">
                    <div v-for="action in filteredRoomActions" :key="action.name">
                      <div
                          class="vac-menu-item"
                          :class="{ 'vac-menu-item-disabled': isActionDisabled(action) }"
                          @click.stop="roomActionHandler(action)"
                      >
                        {{ getActionTitle(action) }}
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </slot>
          </div>
        </div>
        <div class="rom-des-message" v-if="false">
          <!--        <div class="rom-des-message" v-if="room.type === 'single'">-->
          <div style="display: flex;justify-content: center;align-items: center">
            <el-icon :size="13" style="color: #B3B3B3">
              <Location/>
            </el-icon>
            <span class="mess-style">{{ room.location }}</span>
          </div>
          <div style="margin-left: 12px;display: flex;justify-content: center;align-items: center">
            <el-icon :size="13" style="color: #B3B3B3">
              <Clock/>
            </el-icon>
            <span class="mess-style">{{ room.workingHours }}</span>
          </div>
        </div>
      </div>
    </slot>
  </div>
</template>

<script>
import SvgIcon from '@/modules/whatsapp/components/SvgIcon/SvgIcon.vue'
import FormatMessage
  from '@/modules/whatsapp/components/FormatMessage/FormatMessage.vue'

import vClickOutside from '@/modules/whatsapp/utils/on-click-outside'
import typingText from '@/modules/whatsapp/utils/typing-text'
import {isAudioFile} from '@/modules/whatsapp/utils/media-file'
import {formatChatTime} from "@/modules/whatsapp/utils/wa-index";
import {Discount} from "@element-plus/icons-vue";
import MyCustomer from "@/modules/whatsapp/components/MyCustomer/index.vue";

import defaultGroupAvatar from '@/modules/whatsapp/img/default_group_avatar.svg'
import defaultUserAvatar from '@/modules/whatsapp/img/default_user_avatar.svg'
import defaultStatusAvatar from '@/modules/whatsapp/img/default_status_avatar.svg'
import whatsappOfficialAvatar from '@/assets/960px-WhatsApp.svg.webp'

export default {
  name: 'RoomsContent',
  components: {
    MyCustomer,
    Discount,
    SvgIcon,
    FormatMessage
  },

  directives: {
    clickOutside: vClickOutside
  },

  props: {
    currentUserId: {type: [String, Number], required: true},
    room: {type: Object, required: true},
    textFormatting: {type: Object, required: true},
    linkOptions: {type: Object, required: true},
    textMessages: {type: Object, required: true},
    roomActions: {type: Array, required: true}
  },

  emits: ['room-action-handler', 'room-menu-open'],

  data() {
    return {
      roomMenuOpened: null
    }
  },

	mounted() {
		try {
			window.addEventListener('wa-room-menu-open', this.onOtherRoomMenuOpen)
		} catch (e) {
		}
	},

	beforeUnmount() {
		try {
			window.removeEventListener('wa-room-menu-open', this.onOtherRoomMenuOpen)
		} catch (e) {
		}
	},

  computed: {
    isStatusRoom() {
      const rid = String(this.room?.roomId || '').trim()
      return rid === 'status@broadcast'
    },
    isOfficialRoom() {
      const rid = String(this.room?.roomId || '').trim()
      return rid === '0@c.us'
    },
    displayRoomName() {
      if (this.isOfficialRoom) return 'WhatsApp'
      return this.room?.roomName
    },
    isGroupRoom() {
      if (this.room?.isGroup === true) return true
      if (this.room?.type === 'group') return true
      const rid = this.room?.roomId
      return typeof rid === 'string' ? rid.includes('@g.us') : false
    },
    filteredRoomActions() {
      if (this.isStatusRoom) return []
      if (this.isOfficialRoom) return []
      const actions = Array.isArray(this.roomActions) ? this.roomActions : []
      return actions.filter((action) => {
        if (!action) return false
        if (action.name === 'archive') return !this.isGroupRoom
        return true
      })
    },
    resolvedAvatar() {
      if (this.isStatusRoom) return defaultStatusAvatar
      if (this.isOfficialRoom) return whatsappOfficialAvatar
      const avatar = this.room?.avatar
      if (typeof avatar === 'string' && avatar.trim()) return avatar
      return this.isGroupRoom ? defaultGroupAvatar : defaultUserAvatar
    },
    fallbackAvatar() {
      if (this.isStatusRoom) return defaultStatusAvatar
      if (this.isOfficialRoom) return whatsappOfficialAvatar
      return this.isGroupRoom ? defaultGroupAvatar : defaultUserAvatar
    },
    hasCustomAvatar() {
      if (this.isStatusRoom) return false
      if (this.isOfficialRoom) return true
      const avatar = this.room?.avatar
      return typeof avatar === 'string' && avatar.trim().length > 0
    },
    getLastMessage() {
      const isTyping = this.typingUsers
      if (isTyping) return isTyping

      const content = this.room.lastMessage.content

      if (this.room.users.length === 2) {
        return content
      }

      const user = this.room.users.find(
          user => user._id === this.room.lastMessage.senderId
      )

      if (this.room.lastMessage.username) {
        return `${this.room.lastMessage.username} - ${content}`
      } else if (!user || user._id === this.currentUserId) {
        return content
      }

      return `${user.username} - ${content}`
    },
    userStatus() {
      if (!this.room.users || this.room.users.length !== 2) return

      const user = this.room.users.find(u => u._id !== this.currentUserId)
      if (user && user.status) return user.status.state

      return null
    },
    typingUsers() {
      return typingText(this.room, this.currentUserId, this.textMessages)
    },
    isMessageCheckmarkVisible() {
      return (
          !this.typingUsers &&
          this.room.lastMessage &&
          !this.room.lastMessage.deleted &&
          this.room.lastMessage.senderId === this.currentUserId &&
          (this.room.lastMessage.saved ||
              this.room.lastMessage.distributed ||
              this.room.lastMessage.seen)
      )
    },
    formattedDuration() {
      const file = this.room.lastMessage?.files?.[0]
      if (file) {
        if (!file.duration) {
          return `${file.name}.${file.extension}`
        }

        let s = Math.floor(file.duration)
        return (s - (s %= 60)) / 60 + (s > 9 ? ':' : ':0') + s
      }
      return ''
    },
    isAudio() {
      return this.room.lastMessage.files
          ? isAudioFile(this.room.lastMessage.files[0])
          : false
    }
  },

  methods: {
    formatChatTime,
		isActionDisabled(action) {
			if (!action) return false
			try {
				if (typeof action.disabled === 'function') {
					return !!action.disabled(this.room)
				}
				return action.disabled === true
			} catch (e) {
				return false
			}
		},
		onOtherRoomMenuOpen(e) {
			const openedId = e?.detail?.roomId
			if (!openedId) return
			if (this.roomMenuOpened && String(this.roomMenuOpened) !== String(openedId)) {
				this.roomMenuOpened = null
			}
		},
		openRoomMenu() {
			this.roomMenuOpened = this.room.roomId
			this.$emit('room-menu-open', { roomId: this.room.roomId })
			try {
				window.dispatchEvent(
					new CustomEvent('wa-room-menu-open', {
						detail: { roomId: this.room.roomId }
					})
				)
			} catch (e) {
			}
		},
    getActionTitle(action) {
      // 置顶操作根据当前状态显示不同文本
      if (action.name === 'pin') {
        return this.room?.isPinned ? '取消置顶' : '置顶对话'
      }
      return action.title
    },
    roomActionHandler(action) {
      if (this.isActionDisabled(action)) return
      if (action?.name === 'archive' && this.isGroupRoom) return
      this.closeRoomMenu()
      this.$emit('room-action-handler', {action, roomId: this.room.roomId})
    },
    closeRoomMenu() {
      this.roomMenuOpened = null
    }
  }
}
</script>

<style lang="scss" scoped>
.vac-avatar-image {
  border-radius: 50%;
}

.vac-avatar-image :deep(img) {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}
.vac-avatar {
  background-size: 50%;
  background-position: center center;
  background-repeat: no-repeat;
  background-color: #ddd;
  height: 42px;
  width: 42px;
  min-height: 42px;
  min-width: 42px;
  margin-right: 15px;
  border-radius: 50%;
}

.rom-des-message {
  display: flex;
  justify-content: flex-start;
  align-items: center;

  .mess-style {
    font-family: PingFangSC, PingFang SC;
    font-weight: 400;
    font-size: 12px;
    color: #B3B3B3;
    margin-left: 5px;
  }
}

.vac-menu-item.vac-menu-item-disabled {
  cursor: not-allowed;
  color: #B3B3B3;
}
</style>
